import { useCallback, useEffect, useRef, useState } from "react";
import { Menu, Moon, Sun, X } from "lucide-react";
import { useSite } from "@/context/SiteContext";

const scrollToId = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    el.scrollIntoView({ behavior: reduced ? "auto" : "smooth", block: "start" });
};

const LangSwitch = ({ className = "", light = false }) => {
    const { lang, setLang, t } = useSite();
    return (
        <div
            className={`flex items-center gap-1 font-display text-xs font-semibold tracking-[0.14em] ${className}`}
            role="group"
            aria-label={t.a11y.switchLanguage}
            data-testid="language-switcher"
        >
            {["en", "vi"].map((l, i) => (
                <span key={l} className="flex items-center gap-1">
                    {i > 0 && <span aria-hidden="true" className={light ? "text-white/40" : "text-theme-muted"}>/</span>}
                    <button
                        type="button"
                        data-testid={`lang-${l}`}
                        onClick={() => setLang(l)}
                        aria-pressed={lang === l}
                        className={`min-h-[44px] px-2 uppercase transition-colors duration-200 ${
                            lang === l
                                ? light
                                    ? "text-chitta-sky"
                                    : "text-sky-ink"
                                : light
                                  ? "text-white/70 hover:text-white"
                                  : "text-theme-muted hover:text-theme-ink"
                        }`}
                    >
                        {l}
                    </button>
                </span>
            ))}
        </div>
    );
};

const ThemeToggle = ({ light = false }) => {
    const { theme, toggleTheme, t } = useSite();
    return (
        <button
            type="button"
            onClick={toggleTheme}
            aria-label={t.a11y.toggleTheme}
            data-testid="theme-toggle"
            className={`flex h-11 w-11 items-center justify-center rounded-full border transition-colors duration-200 ${
                light
                    ? "border-white/30 text-white hover:border-chitta-sky hover:text-chitta-sky"
                    : "border-theme-strong text-theme-ink hover:border-chitta-sky hover:text-sky-ink"
            }`}
        >
            {theme === "dark" ? <Sun size={17} strokeWidth={1.8} /> : <Moon size={17} strokeWidth={1.8} />}
        </button>
    );
};

export const Header = () => {
    const { t, theme } = useSite();
    const [scrolled, setScrolled] = useState(false);
    const [active, setActive] = useState("");
    const [open, setOpen] = useState(false);
    const menuBtnRef = useRef(null);
    const closeBtnRef = useRef(null);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 32);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        const sections = t.nav
            .map((n) => document.getElementById(n.id))
            .filter(Boolean);
        const obs = new IntersectionObserver(
            (entries) => {
                entries.forEach((e) => {
                    if (e.isIntersecting) setActive(e.target.id);
                });
            },
            { rootMargin: "-40% 0px -55% 0px" },
        );
        sections.forEach((s) => obs.observe(s));
        return () => obs.disconnect();
    }, [t.nav]);

    useEffect(() => {
        if (open) {
            document.body.style.overflow = "hidden";
            closeBtnRef.current?.focus();
        } else {
            document.body.style.overflow = "";
            menuBtnRef.current?.focus?.();
        }
        const onKey = (e) => {
            if (e.key === "Escape") setOpen(false);
        };
        window.addEventListener("keydown", onKey);
        return () => {
            document.body.style.overflow = "";
            window.removeEventListener("keydown", onKey);
        };
    }, [open]);

    const go = useCallback((id) => {
        setOpen(false);
        scrollToId(id);
    }, []);

    const overHero = !scrolled && !open;
    const light = overHero;
    const whiteLogo = overHero || theme === "dark";

    return (
        <>
            <a
                href="#hero"
                data-testid="skip-to-content"
                onClick={(e) => {
                    e.preventDefault();
                    document.getElementById("main")?.focus();
                    scrollToId("main");
                }}
                className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:bg-chitta-sky focus:px-4 focus:py-3 focus:font-display focus:text-sm focus:font-semibold focus:text-chitta-deep"
            >
                {t.a11y.skip}
            </a>
            <header
                data-testid="site-header"
                className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-300 ${
                    overHero
                        ? "border-b border-transparent bg-transparent"
                        : "border-b border-theme bg-header backdrop-blur-md"
                }`}
            >
                <div className="mx-auto flex h-[72px] max-w-[1440px] items-center justify-between gap-4 px-5 sm:px-8 lg:px-12">
                    <button
                        type="button"
                        onClick={() => go("hero")}
                        aria-label={t.a11y.home}
                        data-testid="logo-home-link"
                        className="shrink-0"
                    >
                        <img
                            src={whiteLogo ? "/assets/chitta-logo-white.png" : "/assets/chitta-logo.png"}
                            alt="Chitta Group"
                            className="h-8 w-auto sm:h-9"
                            width="1687"
                            height="480"
                        />
                    </button>

                    <nav aria-label={t.a11y.primaryNav} className="hidden items-center gap-6 lg:flex xl:gap-8">
                        {t.nav.map((n) => (
                            <button
                                key={n.id}
                                type="button"
                                data-testid={`nav-${n.id}`}
                                onClick={() => go(n.id)}
                                aria-current={active === n.id ? "true" : undefined}
                                className={`group relative min-h-[44px] font-display text-sm font-medium lowercase tracking-[0.08em] transition-colors duration-200 ${
                                    light
                                        ? active === n.id
                                            ? "text-chitta-sky"
                                            : "text-white/80 hover:text-white"
                                        : active === n.id
                                          ? "text-sky-ink"
                                          : "text-theme-muted hover:text-theme-ink"
                                }`}
                            >
                                {n.label}
                                <span
                                    aria-hidden="true"
                                    className={`absolute -bottom-0.5 left-0 h-px bg-chitta-sky transition-[width] duration-300 ${
                                        active === n.id ? "w-full" : "w-0 group-hover:w-full"
                                    }`}
                                />
                            </button>
                        ))}
                    </nav>

                    <div className="flex items-center gap-2 sm:gap-3">
                        <LangSwitch light={light} className="hidden sm:flex" />
                        <ThemeToggle light={light} />
                        <button
                            type="button"
                            ref={menuBtnRef}
                            onClick={() => setOpen(true)}
                            aria-label={t.a11y.openMenu}
                            aria-expanded={open}
                            data-testid="mobile-menu-open"
                            className={`flex h-11 w-11 items-center justify-center rounded-full border transition-colors duration-200 lg:hidden ${
                                light
                                    ? "border-white/30 text-white hover:border-chitta-sky hover:text-chitta-sky"
                                    : "border-theme-strong text-theme-ink hover:border-chitta-sky hover:text-sky-ink"
                            }`}
                        >
                            <Menu size={19} strokeWidth={1.8} />
                        </button>
                    </div>
                </div>
            </header>

            {open && (
                <div
                    role="dialog"
                    aria-modal="true"
                    data-testid="mobile-menu"
                    className="fixed inset-0 z-[60] flex flex-col bg-chitta-navy text-white lg:hidden"
                >
                    <div className="flex h-[72px] items-center justify-between px-5 sm:px-8">
                        <img src="/assets/chitta-logo-white.png" alt="Chitta Group" className="h-8 w-auto" width="1687" height="480" />
                        <button
                            type="button"
                            ref={closeBtnRef}
                            onClick={() => setOpen(false)}
                            aria-label={t.a11y.closeMenu}
                            data-testid="mobile-menu-close"
                            className="flex h-11 w-11 items-center justify-center rounded-full border border-white/30 text-white transition-colors duration-200 hover:border-chitta-sky hover:text-chitta-sky"
                        >
                            <X size={19} strokeWidth={1.8} />
                        </button>
                    </div>
                    <nav aria-label={t.a11y.primaryNav} className="flex flex-1 flex-col justify-center gap-1 px-6 sm:px-10">
                        {t.nav.map((n, i) => (
                            <button
                                key={n.id}
                                type="button"
                                data-testid={`mobile-nav-${n.id}`}
                                onClick={() => go(n.id)}
                                className="group flex items-baseline gap-4 border-b border-white/10 py-4 text-left"
                            >
                                <span className="font-display text-xs text-chitta-sky">0{i + 3}</span>
                                <span className="font-display text-3xl font-medium lowercase tracking-tight text-white transition-colors duration-200 group-hover:text-chitta-sky sm:text-4xl">
                                    {n.label}
                                </span>
                            </button>
                        ))}
                    </nav>
                    <div className="flex items-center justify-between px-6 pb-10 sm:px-10">
                        <LangSwitch light />
                        <ThemeToggle light />
                    </div>
                </div>
            )}
        </>
    );
};
